import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';

import {
  Box, Card, CardContent, Typography, LinearProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Skeleton, Alert, Tooltip, Collapse,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const BUCKET_COLOR = {
  S1: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  S2: { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  S3: { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  M1: { bg: '#fce4ec', text: '#c62828', border: '#f48fb1' },
  M2: { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' },
  X1: { bg: '#e8eaf6', text: '#283593', border: '#9fa8da' },
  X2: { bg: '#e0e0e0', text: '#424242', border: '#bdbdbd' },
  WO: { bg: '#212121', text: '#ffffff', border: '#424242' },
};

function DonutChart({ pct, size = 72, strokeWidth = 8, color = '#d32028' }) {
  const r = (size - strokeWidth) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = Math.min(pct / 100, 1) * circ;
  const barColor = pct >= 75 ? '#2e7d32' : pct >= 40 ? '#f57f17' : '#d32028';
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={c} cy={c} r={r} fill="none" stroke="#f0f0f0" strokeWidth={strokeWidth} />
      <circle
        cx={c} cy={c} r={r} fill="none"
        stroke={color || barColor} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text x={c} y={c + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize="13" fontWeight="700" fill="#344054">
        {pct}%
      </text>
    </svg>
  );
}

function StatCard({ icon, label, value, sub, color = '#344054', accentColor = '#667eea' }) {
  return (
    <Card elevation={0} sx={{
      flex: 1, minWidth: 130,
      border: '1px solid #f0f0f0',
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: '10px',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
    }}>
      <CardContent sx={{ p: '14px 16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color: accentColor, display: 'flex', fontSize: 16 }}>{icon}</Box>
          <Typography sx={{ fontSize: '10px', color: '#667085', fontFamily: 'Inter-Medium', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color, fontFamily: 'Inter-Medium', lineHeight: 1.2 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '11px', color: '#9e9e9e', mt: 0.3 }}>{sub}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function AgentDashboard({ agentId, initialData }) {
  // initialData only applies for the self-view (no agentId); manager peek always fetches fresh
  const [data, setData]         = useState(!agentId && initialData?.dashboardRaw ? initialData.dashboardRaw : null);
  const [loading, setLoading]   = useState(agentId ? true : !initialData);
  const [error, setError]       = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiOpen, setAiOpen]     = useState(true);

  useEffect(() => {
    const url = agentId
      ? `/collection-incentive/manager/agents/${agentId}/dashboard`
      : '/collection-incentive/agent/dashboard';

    if (!agentId && initialData) return;

    axiosInstance.get(url)
      .then((res) => setData(res.data.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (agentId || !initialData) return;
    setData(initialData.dashboardRaw);
    setLoading(false);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire-and-forget — 404 is expected until backend is wired. Never blocks dashboard render.
  useEffect(() => {
    axiosInstance.get('/collection-incentive/agent/insights')
      .then((res) => setAiInsights(res.data?.data || null))
      .catch(() => setAiInsights(null));
  }, [agentId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={48} sx={{ mb: 2, borderRadius: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={90} sx={{ flex: 1, borderRadius: 2 }} />)}
        </Box>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  const { rotation, stats, bucketProgress, priorityQueue } = data || {};
  const s = stats || {};
  const daysLeft = rotation?.daysUntilRotation;

  return (
    <Box sx={{ p: '10px' }}>

      {/* ── Rotation Banner ── */}
      {rotation?.nextRotationDate && (
        <Alert
          icon={<AccessTimeIcon fontSize="small" />}
          severity={daysLeft <= 3 ? 'error' : daysLeft <= 7 ? 'warning' : 'info'}
          sx={{
            mb: 2, borderRadius: 2, fontFamily: 'Inter-Medium', fontSize: '13px',
            alignItems: 'center',
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
              Next rotation: <strong>{rotation.nextRotationDate}</strong> — Mark PTP to retain loans.
            </span>
            {daysLeft != null && (
              <Chip
                label={`${daysLeft} day${daysLeft !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: '11px', ml: 2,
                  backgroundColor: daysLeft <= 3 ? '#d32028' : daysLeft <= 7 ? '#f57f17' : '#1976d2',
                  color: 'white',
                }}
              />
            )}
          </Box>
        </Alert>
      )}

      {/* ── 6 Stat Cards ── */}
      <Box sx={{ display: 'flex', gap: '12px', mb: 3, flexWrap: 'wrap' }}>

        {/* Collection % — donut chart card */}
        <Card elevation={0} sx={{
          minWidth: 150, border: '1px solid #f0f0f0', borderLeft: '3px solid #d32028',
          borderRadius: '10px', transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
        }}>
          <CardContent sx={{ p: '14px 16px !important' }}>
            <Typography sx={{ fontSize: '10px', color: '#667085', fontFamily: 'Inter-Medium', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1 }}>
              Collection %
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DonutChart pct={s.collectionPct || 0} />
              <Box>
                <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>
                  {fmt(s.recovered)} / {fmt((s.recovered || 0) + (s.pending || 0))}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <StatCard
          icon={<TrendingUpIcon fontSize="inherit" />}
          label="Loans"
          value={s.loans || 0}
          accentColor="#667eea"
        />
        <StatCard
          icon={<AccountBalanceWalletIcon fontSize="inherit" />}
          label="Recovered"
          value={fmt(s.recovered)}
          color="#2e7d32"
          accentColor="#2e7d32"
        />
        <StatCard
          icon={<HourglassEmptyIcon fontSize="inherit" />}
          label="Pending"
          value={fmt(s.pending)}
          color="#d32028"
          accentColor="#f57f17"
        />
        <StatCard
          icon={<HandshakeIcon fontSize="inherit" />}
          label="PTP"
          value={s.ptpCount || 0}
          accentColor="#9c27b0"
        />
        <StatCard
          icon={<EmojiEventsIcon fontSize="inherit" />}
          label="Incentive"
          value={fmt(s.incentiveEarned)}
          color="#2e7d32"
          accentColor="#2e7d32"
        />
      </Box>

      {/* ── AI Insights ── */}
      <Box sx={{ mb: 3 }}>
        {/* Header row — click to collapse */}
        <Box
          onClick={() => setAiOpen((o) => !o)}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            border: '1px solid #bbf7d0',
            borderRadius: aiOpen ? '10px 10px 0 0' : '10px',
            px: 2, py: '10px',
            cursor: 'pointer', userSelect: 'none',
            transition: 'border-radius 0.2s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '15px' }}>🤖</span>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#166534', fontFamily: 'Inter-Medium' }}>
              AI Insights
            </Typography>
            <Box sx={{
              fontSize: '9px', px: '6px', py: '2px',
              background: '#16a34a', color: 'white',
              borderRadius: '8px', fontWeight: 700, lineHeight: 1.4,
            }}>
              AI
            </Box>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#16a34a', fontWeight: 600 }}>
            {aiOpen ? '▲ Hide' : '▼ Show'}
          </Typography>
        </Box>

        <Collapse in={aiOpen}>
          <Box sx={{
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            border: '1px solid #bbf7d0',
            borderTop: 'none',
            borderRadius: '0 0 10px 10px',
            px: 2, pt: '2px', pb: '12px',
          }}>
            {/* 4 stat cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', mb: 1 }}>

              {/* Recoverable Today */}
              <Tooltip title={aiInsights ? '' : 'Coming soon — scores computed nightly'} placement="top" arrow>
                <Box sx={{
                  background: 'white', borderRadius: '6px', p: '10px 12px',
                  border: '1px solid #bbf7d0', textAlign: 'center',
                  cursor: aiInsights ? 'default' : 'help',
                }}>
                  <Typography sx={{ fontSize: '9px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                    Recoverable Today
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: aiInsights ? '#166534' : '#9e9e9e', lineHeight: 1.2 }}>
                    {aiInsights?.recoverableToday != null ? fmt(aiInsights.recoverableToday) : '—'}
                  </Typography>
                  {aiInsights?.recoverableCount != null && (
                    <Typography sx={{ fontSize: '9px', color: '#059669', mt: '2px' }}>
                      {aiInsights.recoverableCount} loans
                    </Typography>
                  )}
                </Box>
              </Tooltip>

              {/* High Priority */}
              <Tooltip title={aiInsights ? '' : 'Coming soon — recovery probability > 70%'} placement="top" arrow>
                <Box sx={{
                  background: 'white', borderRadius: '6px', p: '10px 12px',
                  border: '1px solid #bbf7d0', textAlign: 'center',
                  cursor: aiInsights ? 'default' : 'help',
                }}>
                  <Typography sx={{ fontSize: '9px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                    High Priority
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: aiInsights ? '#166534' : '#9e9e9e', lineHeight: 1.2 }}>
                    {aiInsights?.highPriority != null ? aiInsights.highPriority : '—'}
                  </Typography>
                  {aiInsights?.highPriority != null && (
                    <Typography sx={{ fontSize: '9px', color: '#059669', mt: '2px' }}>
                      {'>'} 70% prob
                    </Typography>
                  )}
                </Box>
              </Tooltip>

              {/* Best Call Time */}
              <Tooltip title={aiInsights ? '' : 'Coming soon — historical connect rate analysis'} placement="top" arrow>
                <Box sx={{
                  background: 'white', borderRadius: '6px', p: '10px 12px',
                  border: '1px solid #bbf7d0', textAlign: 'center',
                  cursor: aiInsights ? 'default' : 'help',
                }}>
                  <Typography sx={{ fontSize: '9px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                    Best Call Time
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: aiInsights ? '#166534' : '#9e9e9e', lineHeight: 1.2 }}>
                    {aiInsights?.bestCallTime ?? '—'}
                  </Typography>
                  {aiInsights?.connectRate != null && (
                    <Typography sx={{ fontSize: '9px', color: '#059669', mt: '2px' }}>
                      {aiInsights.connectRate}% connect
                    </Typography>
                  )}
                </Box>
              </Tooltip>

              {/* AI Accuracy */}
              <Tooltip title={aiInsights ? '' : 'Coming soon — yesterday\'s prediction accuracy'} placement="top" arrow>
                <Box sx={{
                  background: 'white', borderRadius: '6px', p: '10px 12px',
                  border: '1px solid #bbf7d0', textAlign: 'center',
                  cursor: aiInsights ? 'default' : 'help',
                }}>
                  <Typography sx={{ fontSize: '9px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                    AI Accuracy
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: aiInsights ? '#166534' : '#9e9e9e', lineHeight: 1.2 }}>
                    {aiInsights?.aiAccuracy != null ? `${aiInsights.aiAccuracy}%` : '—'}
                  </Typography>
                  {aiInsights?.aiAccuracy != null && (
                    <Typography sx={{ fontSize: '9px', color: '#059669', mt: '2px' }}>
                      Yesterday
                    </Typography>
                  )}
                </Box>
              </Tooltip>

            </Box>

            {/* Footer note */}
            <Typography sx={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', pt: '2px' }}>
              Scores computed via daily cron (6 AM) • No AI credits consumed when viewing
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* ── My Bucket Progress ── */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', fontWeight: 600, mb: 1.5 }}>
          📊 My Bucket Progress
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                {['Bucket', 'Loans', 'Total Overdue', 'Recovered', 'Collection %', 'Target', 'Gap', 'Incentive'].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, color: '#667085', fontSize: '12px', whiteSpace: 'nowrap', py: '10px' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(bucketProgress || []).map((row) => {
                const bc = BUCKET_COLOR[row.bucket] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
                const pct = row.collectionPct || 0;
                const barColor = pct >= (row.target || 100) ? '#2e7d32' : pct >= 50 ? '#f57f17' : '#d32028';
                return (
                  <TableRow key={row.bucket} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell>
                      <Chip
                        label={row.bucket}
                        size="small"
                        sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px', border: `1px solid ${bc.border}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#344054' }}>{row.loans}</TableCell>
                    <TableCell sx={{ color: '#d32028', fontWeight: 600 }}>
                      {row.overdue > 0 ? fmt(row.overdue) : <span style={{ color: '#bdbdbd' }}>₹0</span>}
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      {row.recovered > 0 ? fmt(row.recovered) : <span style={{ color: '#bdbdbd' }}>₹0</span>}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(pct, 100)}
                          sx={{
                            flex: 1, height: 8, borderRadius: 4,
                            backgroundColor: '#f0f0f0',
                            '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 4 },
                          }}
                        />
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: barColor, minWidth: 32 }}>
                          {pct}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#667085', fontSize: '12px' }}>
                      {row.target !== null ? `${row.target}%` : 'Per File'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px' }}>
                      {row.gap !== null && row.gap > 0
                        ? <Typography sx={{ color: '#d32028', fontWeight: 600, fontSize: '12px' }}>{fmt(row.gap)}</Typography>
                        : row.gap === 0
                        ? <Typography sx={{ color: '#2e7d32', fontSize: '16px' }}>✓</Typography>
                        : <span style={{ color: '#bdbdbd' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px' }}>
                      {row.incentive !== null && row.incentive > 0
                        ? <Typography sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '12px' }}>{fmt(row.incentive)}</Typography>
                        : <span style={{ color: '#bdbdbd' }}>—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Priority Queue ── */}
      {priorityQueue && priorityQueue.filter((l) => Number(l.overdue) > 0).length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', fontWeight: 600 }}>
              🔥 Priority Queue
            </Typography>
            <Chip
              label="AI Ranked"
              size="small"
              sx={{ fontSize: '10px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
            />
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            {priorityQueue
              .filter((l) => Number(l.overdue) > 0)
              .map((loan, i, arr) => {
                const bc = BUCKET_COLOR[loan.bucket] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
                const dpd = Number(loan.dpd) || 0;
                const overdue = Number(loan.overdue) || 0;

                const recovProb  = dpd <= 15 ? 80 : dpd <= 45 ? 60 : dpd <= 90 ? 38 : 20;
                const probBg     = recovProb >= 70 ? '#dcfce7' : recovProb >= 50 ? '#fef9c3' : '#fee2e2';
                const probText   = recovProb >= 70 ? '#166534' : recovProb >= 50 ? '#92400e' : '#991b1b';
                const probBorder = recovProb >= 70 ? '#86efac' : recovProb >= 50 ? '#fde68a' : '#fca5a5';

                const strategy = dpd <= 7  ? '📱 WhatsApp + eMandate nudge'
                               : dpd <= 30 ? '📞 Call + WhatsApp combo'
                               : dpd <= 60 ? '📞 Persistent calling'
                               : dpd <= 90 ? '🤝 Settlement offer'
                               :             '⚖️ Legal notice';

                const bestTime = aiInsights?.bestCallTime || '10 AM – 12 PM';

                return (
                  <Box
                    key={loan.loanId || i}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      px: 2, py: '8px',
                      borderBottom: i < arr.length - 1 ? '1px solid #f5f5f5' : 'none',
                      '&:hover': { backgroundColor: '#fafbfc' },
                    }}
                  >
                    {/* Probability badge */}
                    <Box sx={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: probBg, border: `1.5px solid ${probBorder}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: probText, lineHeight: 1 }}>
                        {recovProb}%
                      </Typography>
                      <Typography sx={{ fontSize: '8px', color: probText, opacity: 0.7, lineHeight: 1.2 }}>
                        prob
                      </Typography>
                    </Box>

                    {/* Loan info — 2 tight lines */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Line 1: Loan ID + bucket + DPD + borrower */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                        <Tooltip title={loan.loanId || ''} placement="top" arrow>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1d2939', cursor: 'default' }}>
                            {loan.legacyLoanId || loan.loanId}
                          </Typography>
                        </Tooltip>
                        <Chip label={loan.bucket} size="small" sx={{
                          height: '15px', fontSize: '10px', fontWeight: 700,
                          backgroundColor: bc.bg, color: bc.text, border: `1px solid ${bc.border}`,
                          '& .MuiChip-label': { px: '4px' },
                        }} />
                        <Typography sx={{ fontSize: '11px', color: '#9e9e9e' }}>DPD: {dpd}</Typography>
                        {(loan.ptpDate || loan.ptp) && (
                          <Box sx={{
                            display: 'inline-block', px: '6px', py: '1px', borderRadius: '4px',
                            fontSize: '10px', fontWeight: 700,
                            backgroundColor: '#FFF9C4', color: '#F57F17',
                          }}>
                            PTP
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '11px', color: '#475467' }}>· {loan.borrowerName?.trim() || '—'}</Typography>
                      </Box>
                      {/* Line 2: strategy + best call time */}
                      <Typography sx={{ fontSize: '10px', color: '#059669', mt: '2px' }}>
                        {strategy} · 📞 Best: {bestTime}
                      </Typography>
                    </Box>

                    {/* Overdue */}
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#d32028', flexShrink: 0 }}>
                      {fmt(overdue)}
                    </Typography>
                  </Box>
                );
              })}
          </Paper>
        </Box>
      )}

    </Box>
  );
}
